import { useState, useEffect, useCallback } from "react";
import { X, Loader2, History, RefreshCw, ChevronLeft, ChevronRight } from "lucide-react";
import spreadsheetConnectorService, {
  SyncHistoryRecord,
  SyncHistoryStatus,
  SyncTriggerType,
} from "../../../services/spreadsheet-connector.service";

interface SyncHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  connectorId: string;
  spreadsheetName: string;
}

const PAGE_SIZE = 15;

const STATUS_STYLES: Record<SyncHistoryStatus, string> = {
  [SyncHistoryStatus.SUCCESS]: "bg-green-100 text-green-700",
  [SyncHistoryStatus.FAILED]:  "bg-red-100 text-red-700",
  [SyncHistoryStatus.SKIPPED]: "bg-gray-100 text-gray-500",
};

const TRIGGER_STYLES: Record<SyncTriggerType, string> = {
  [SyncTriggerType.INITIAL]: "bg-blue-100 text-blue-700",
  [SyncTriggerType.WEBHOOK]: "bg-purple-100 text-purple-700",
  [SyncTriggerType.RESUME]:  "bg-yellow-100 text-yellow-700",
  [SyncTriggerType.MANUAL]:  "bg-gray-100 text-gray-600",
};

export default function SyncHistoryModal({
  isOpen,
  onClose,
  connectorId,
  spreadsheetName,
}: SyncHistoryModalProps) {
  const [records, setRecords] = useState<SyncHistoryRecord[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  const [filterStatus, setFilterStatus]   = useState<string>("");
  const [filterTrigger, setFilterTrigger] = useState<string>("");
  const [fromDate, setFromDate]           = useState("");
  const [toDate, setToDate]               = useState("");

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const fetchHistory = useCallback(async () => {
    if (!connectorId) return;
    setLoading(true);
    try {
      const result = await spreadsheetConnectorService.listSyncHistory({
        connector_id: connectorId,
        ...(filterStatus  ? { status:       filterStatus  as SyncHistoryStatus }  : {}),
        ...(filterTrigger ? { trigger_type: filterTrigger as SyncTriggerType }    : {}),
        ...(fromDate      ? { from_date: new Date(fromDate).toISOString() }       : {}),
        ...(toDate        ? { to_date:   new Date(toDate + "T23:59:59").toISOString() } : {}),
        skip:  (page - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
      });
      setRecords(result.data);
      setTotalCount(result.total_count);
    } catch {
      // silent — table just shows empty
    } finally {
      setLoading(false);
    }
  }, [connectorId, filterStatus, filterTrigger, fromDate, toDate, page]);

  useEffect(() => {
    if (isOpen) fetchHistory();
  }, [isOpen, fetchHistory]);

  const resetFilters = () => {
    setFilterStatus("");
    setFilterTrigger("");
    setFromDate("");
    setToDate("");
    setPage(1);
  };

  const formatDate = (iso?: string) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit", hour12: true,
    });
  };

  const formatDuration = (seconds?: number) => {
    if (seconds == null) return "—";
    if (seconds < 1) return "<1s";
    return `${seconds.toFixed(1)}s`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl mx-4 flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
              <History className="w-4 h-4 text-green-600" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Sync History</h2>
              <p className="text-xs text-gray-400 truncate max-w-xs">{spreadsheetName}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Filters */}
        <div className="px-6 py-3 border-b border-gray-100 flex items-center gap-3 flex-wrap flex-shrink-0">
          <select
            value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
            className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 text-gray-600 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <option value="">All statuses</option>
            <option value={SyncHistoryStatus.SUCCESS}>Success</option>
            <option value={SyncHistoryStatus.FAILED}>Failed</option>
            <option value={SyncHistoryStatus.SKIPPED}>Skipped</option>
          </select>

          <select
            value={filterTrigger}
            onChange={(e) => { setFilterTrigger(e.target.value); setPage(1); }}
            className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 text-gray-600 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <option value="">All triggers</option>
            <option value={SyncTriggerType.INITIAL}>Initial</option>
            <option value={SyncTriggerType.WEBHOOK}>Webhook</option>
            <option value={SyncTriggerType.RESUME}>Resume</option>
            <option value={SyncTriggerType.MANUAL}>Manual</option>
          </select>

          <input
            type="date"
            value={fromDate}
            onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
            className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 text-gray-600 focus:outline-none focus:ring-2 focus:ring-green-500"
          />
          <span className="text-xs text-gray-400">to</span>
          <input
            type="date"
            value={toDate}
            onChange={(e) => { setToDate(e.target.value); setPage(1); }}
            className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 text-gray-600 focus:outline-none focus:ring-2 focus:ring-green-500"
          />

          {(filterStatus || filterTrigger || fromDate || toDate) && (
            <button
              onClick={resetFilters}
              className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1 transition-colors"
            >
              <RefreshCw className="w-3 h-3" /> Reset
            </button>
          )}

          <div className="ml-auto flex items-center gap-1.5">
            <span className="text-xs text-gray-400">{totalCount} records</span>
            <button
              onClick={fetchHistory}
              className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-auto flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-16 gap-2 text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm">Loading history…</span>
            </div>
          ) : records.length === 0 ? (
            <div className="text-center py-16">
              <History className="w-8 h-8 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">No sync records found</p>
              <p className="text-xs text-gray-400 mt-1">
                {filterStatus || filterTrigger || fromDate || toDate
                  ? "Try adjusting your filters"
                  : "Sync history will appear here after the first sync"}
              </p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white border-b border-gray-100 z-10">
                <tr>
                  <th className="text-left px-6 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">Trigger</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">Started</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">Rows Changed</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">Sheets Synced</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">Error</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {records.map((r) => (
                  <tr key={r._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[r.status]}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${TRIGGER_STYLES[r.trigger_type]}`}>
                        {r.trigger_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">
                      {formatDate(r.start_time)}
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">
                      {formatDuration(r.time_taken)}
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">
                      {r.rows_changed != null ? r.rows_changed.toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs max-w-[180px] truncate" title={r.sheets_synced?.join(", ")}>
                      {r.sheets_synced?.length ? r.sheets_synced.join(", ") : "—"}
                    </td>
                    <td className="px-4 py-3 text-xs max-w-[200px]">
                      {r.error ? (
                        <span
                          className="text-red-500 truncate block cursor-help"
                          title={r.error}
                        >
                          {r.error}
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100 flex-shrink-0">
            <span className="text-xs text-gray-500">
              Page {page} of {totalPages}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
